/**
 * Enhanced Technology Chart Component
 * 
 * Generic enhanced chart with configurable modes for different technologies:
 * - Monitron: Dual charts (velocity + temperature) with toggle
 * - CADET: Single chart (current) 
 * - Future: Other technologies as needed
 * 
 * Features: Actual/Trend data toggle, ISO lines, brush zoom, professional styling
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  VictoryChart,
  VictoryAxis,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import DateRangePicker from "@amzn/meridian/date-range-picker";
import Badge from '@amzn/meridian/badge';
import useVictoryTheme from '@amzn/meridian/use-victory-theme';
import { TrendChartData, MeasurementDataPoint, TechnologyType, DateRange } from '../../../services/equipment/types';
import { useDateRange } from '../../../hooks/use-date-range';
import { useTrendChartData } from '../../../hooks/use-trend-chart-data';
import { useVictoryZoom } from '../../../hooks/use-victory-zoom';
import { addDays, formatDate } from '../date-range-selector/date-range-utils';

/**
 * Series configuration for different chart types
 */
interface SeriesConfig {
  name: string;
  label: string;
  color: string;
  unit: string;
}

/**
 * Technology-specific configuration
 */
interface TechnologyConfig {
  chartMode: 'single' | 'dual' | 'multi';
  series: (SeriesConfig & { axis?: 'left' | 'right' })[];
  showIsoLines: boolean;
  isoLineValue?: number;
  showGraphToggle: boolean;
  showMultiSeriesToggle?: boolean;
  defaultVisibleSeries?: string[];
  dualAxis?: boolean;
  displayName: string;
}

/**
 * Technology configurations
 */
const technologyConfigs: Record<string, TechnologyConfig> = {
  monitron: {
    chartMode: 'multi',
    series: [
      { name: 'vibration', label: 'RMS', color: '#00C49F', unit: 'mm/s', axis: 'left' },
      { name: 'temperature', label: 'Temperature', color: '#EB63D7', unit: '°C', axis: 'right' },
      { name: 'vel_x', label: 'X-axis', color: '#4A90E2', unit: 'mm/s', axis: 'left' },
      { name: 'vel_y', label: 'Y-axis', color: '#F39C12', unit: 'mm/s', axis: 'left' },
      { name: 'vel_z', label: 'Z-axis', color: '#9B59B6', unit: 'mm/s', axis: 'left' }
    ],
    showIsoLines: true,
    isoLineValue: 0.2,
    showGraphToggle: false,
    showMultiSeriesToggle: true,
    defaultVisibleSeries: ['vibration', 'temperature'],
    dualAxis: true,
    displayName: 'Monitron'
  },
  cadet: {
    chartMode: 'single',
    series: [
      { name: 'current', label: 'Current', color: '#00C49F', unit: 'A' }
    ],
    showIsoLines: false,
    showGraphToggle: false,
    displayName: 'CADET'
  }
};

export interface EnhancedTechnologyChartProps {
  technology: TechnologyType;
  /** Chart data containing measurement series */
  data: TrendChartData | null;
  containerWidth?: number;
  isLoading?: boolean;
  /** Error state */
  error?: string | null;
  /** Chart height */
  height?: number;
  site?: string;
  equipmentId?: string;
  position?: string;
}

/**
 * Data mode types for toggle functionality
 */
type DataMode = 'actual' | 'trend';

/**
 * Zero filter mode types for filtering low values
 */
type ZeroFilterMode = 'include' | 'exclude';

/**
 * Chart series data structure
 */
interface ChartSeries {
  seriesData: Record<string, MeasurementDataPoint[]>;
  seriesLabels: Record<string, string>;
}

/**
 * Data processing utilities
 */
const chartDataUtils = {
  /**
   * Downsample data for trend mode (right now we are keeping every nth point)
   */
  downsampleData: (data: MeasurementDataPoint[], factor: number = 4): MeasurementDataPoint[] => {
    if (data.length <= 50) return data; // WE are not downsampled the data points which have length less than 50
    
    if (data.length > 500) {
      factor = Math.max(factor, Math.floor(data.length / 100)); // Keep around 100 points max
    }
    
    return data.filter((_, index) => index % factor === 0);
  },


  /**
   * Extract series data based on technology configuration
   */
  extractChartSeries: (data: TrendChartData, config: TechnologyConfig, technology: TechnologyType): ChartSeries | null => {
    if (!data.timeSeries || data.timeSeries.length === 0) {
      return null;
    }

    const seriesData: Record<string, MeasurementDataPoint[]> = {};
    const seriesLabels: Record<string, string> = {};

    // For each expected series, try to find matching data by key first, then by label
    config.series.forEach((seriesConfig, index) => {
      let foundSeries = null;

      // First try to match by key
      foundSeries = data.timeSeries.find(series => series.key === seriesConfig.name);
      
      if (!foundSeries && technology === 'cadet') {
        foundSeries = data.timeSeries.find(series =>
          series.label.toLowerCase().includes('current') ||
          series.label.toLowerCase().includes('amp')
        );
      }

      // Final fallback: use series by index if no match found
      if (!foundSeries && data.timeSeries[index]) {
        foundSeries = data.timeSeries[index];
      }

      if (foundSeries) {
        seriesData[seriesConfig.name] = foundSeries.data;
        seriesLabels[seriesConfig.name] = foundSeries.label;
      } else {
        seriesData[seriesConfig.name] = [];
        seriesLabels[seriesConfig.name] = seriesConfig.label;
      }
    });

    return { seriesData, seriesLabels };
  }
};

/**
 * Empty state component
 */
const ChartEmptyState: React.FC<{ config: TechnologyConfig }> = ({ config }) => (
  <Column alignmentHorizontal="center" spacing="200">
    <Text type="h400">{config.displayName}</Text>
    <Text>No measurement data available for the selected date range.</Text>
  </Column>
);

/**
 * Loading state component
 */
const ChartLoadingState: React.FC<{ config: TechnologyConfig }> = ({ config }) => (
  <Column alignmentHorizontal="center" spacing="200">
    <Text type="h400">{config.displayName}</Text>
    <Text>Loading measurement data...</Text>
  </Column>
);

/**
 * Error state component
 */
const ChartErrorState: React.FC<{ config: TechnologyConfig; error: string }> = ({ config, error }) => (
  <Column spacing="200">
    <Text type="h400">{config.displayName}</Text>
    <Alert type="error" size="small">
      Failed to load {config.displayName.toLowerCase()} data: {error}
    </Alert>
  </Column>
);

/**
 * Enhanced Technology Chart Component
 */
export const EnhancedTechnologyChart: React.FC<EnhancedTechnologyChartProps> = ({
  technology,
  data,
  containerWidth = 800,
  isLoading = false,
  error = null,
  height = 400,
  site,
  equipmentId,
  position,
}) => {
  const theme = useVictoryTheme({ showIndependentGrid: true });
  
  const config = technologyConfigs[technology] || technologyConfigs.cadet; // fallback to cadet
  
  // ISO line color
  const isoLineColor = '#FFC658';
  
  // Page-level date range from context
  const { dateRange: pageDateRange } = useDateRange();
  
  // State management
  const [dataMode, setDataMode] = useState<DataMode>('actual');
  const [zeroFilterMode, setZeroFilterMode] = useState<ZeroFilterMode>('include');
  const [currentGraphType, setCurrentGraphType] = useState<string>(config.series[0]?.name || '');
  const [visibleSeries, setVisibleSeries] = useState<string[]>(config.defaultVisibleSeries || [config.series[0]?.name || '']);
  const [chartDateOverride, setChartDateOverride] = useState<DateRange | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Handle multi-series toggle
  const toggleSeries = useCallback((seriesName: string) => {
    setVisibleSeries(prev => {
      if (prev.includes(seriesName)) {
        if (prev.length === 1) return prev;
        return prev.filter(name => name !== seriesName);
      } else {
        return [...prev, seriesName];
      }
    });
  }, []);

  // Determine current date range
  const currentDateRange = chartDateOverride || pageDateRange;
  const isUsingPageDate = !chartDateOverride;

  // Chart-specific data loading hook (only active when overridden, only used when user use monitron, cadet dae picker)
  const {
    data: hookData,
    fetchData: fetchChartData,
    isLoading: isChartLoading,
    error: hookError
  } = useTrendChartData({
    site,
    equipmentId,
    position,
    technology,
    autoFetch: false,
    useDateRangeFiltering: false
  });

  // Use hook data when chart has date override, parent data otherwise
  const effectiveData = chartDateOverride ? hookData : data;
  const effectiveLoading = chartDateOverride ? isChartLoading : isLoading;
  const effectiveError = chartDateOverride ? hookError : error;

  // Use the reusable zoom hook with reset capability
  const { zoomContainer, zoomDisplayText, resetZoom } = useVictoryZoom({
    enableZoom: true,
    minimumZoom: 0.1,
    allowPan: true,
    allowZoom: true,
  });

  // Reset zoom when data mode or graph type changes
  useEffect(() => {
    console.log(`Resetting zoom: dataMode=${dataMode}, graphType=${currentGraphType}`);
    resetZoom();
  }, [dataMode, currentGraphType, resetZoom]);

  // Date picker presets
  const today = formatDate(new Date());
  const datePresets = [
    { label: "Last 1 days", value: [formatDate(addDays(new Date(), -1)), today] },
    { label: "Last 1 week", value: [formatDate(addDays(new Date(), -7)), today] },
    { label: "Last 1 month", value: [formatDate(addDays(new Date(), -30)), today] },
    { label: "Last 3 months", value: [formatDate(addDays(new Date(), -90)), today] },
  ];

  // Extract and process chart data - use effective data
  const chartSeries = useMemo(() => {
    if (!effectiveData) return null;
    return chartDataUtils.extractChartSeries(effectiveData, config, technology);
  }, [effectiveData, config, technology]);

  // Process data based on current mode and zero filtering (handle enhanced chart all filtering)
  const processedData = useMemo(() => {
    if (!chartSeries) return null;
    
    const processed: Record<string, MeasurementDataPoint[]> = {};
    
    Object.keys(chartSeries.seriesData).forEach(seriesName => {
      let data = chartSeries.seriesData[seriesName];
      
      if (dataMode === 'trend') {
        data = chartDataUtils.downsampleData(data, 4);
      }
      
      if (zeroFilterMode === 'exclude') {
        data = data.filter(point => point.y >= 0.1);
      }
      
      processed[seriesName] = data;
    });

    return processed;
  }, [chartSeries, dataMode, zeroFilterMode]);

  // Get visible series data for multi-series rendering
  const visibleSeriesData = useMemo(() => {
    if (!processedData || !chartSeries) return [];
    
    if (config.chartMode === 'single') {
      const firstSeriesName = config.series[0]?.name;
      if (!firstSeriesName) return [];
      
      return [{
        data: processedData[firstSeriesName] || [],
        label: chartSeries.seriesLabels[firstSeriesName] || config.series[0].label,
        color: config.series[0].color,
        unit: config.series[0].unit,
        name: firstSeriesName,
        axis: config.series[0].axis || 'left'
      }];
    } else if (config.chartMode === 'multi') {
      // Multi-series mode: return all visible series
      return config.series
        .filter(series => visibleSeries.includes(series.name))
        .map(series => ({
          data: processedData[series.name] || [],
          label: chartSeries.seriesLabels[series.name] || series.label,
          color: series.color,
          unit: series.unit,
          name: series.name,
          axis: series.axis || 'left'
        }));
    } else {
      // Dual chart mode: use selected graph type
      const selectedSeries = config.series.find(s => s.name === currentGraphType);
      if (!selectedSeries) return [];
      
      return [{
        data: processedData[selectedSeries.name] || [],
        label: chartSeries.seriesLabels[selectedSeries.name] || selectedSeries.label,
        color: selectedSeries.color,
        unit: selectedSeries.unit,
        name: selectedSeries.name,
        axis: selectedSeries.axis || 'left'
      }];
    }
  }, [processedData, chartSeries, config, currentGraphType, visibleSeries]);

  const currentGraphData = visibleSeriesData[0] || null;

  // Generate simple ISO line data that spans full chart width
  const isoLineData = useMemo(() => {
    if (!config.showIsoLines || !config.isoLineValue) return null;
    
    // Only show ISO line for velocity graph 
    //TODO: Need to confirm with team if we want to show isoLine for temperature.
    if (currentGraphData?.name === 'temperature') return null;
  
    return [
      { x: 0, y: config.isoLineValue },
      { x: 1000, y: config.isoLineValue }
    ];
  }, [config.showIsoLines, config.isoLineValue, currentGraphData?.name]);

  const handleDateRangeChange = useCallback(async (dates: string[]) => {
    if (dates.length === 2 && site && equipmentId && position) {
      const newDateRange: DateRange = {
        startDate: dates[0],
        endDate: dates[1]
      };
      setChartDateOverride(newDateRange);
      setShowDatePicker(false);
      
      // Trigger chart-specific API call with new date range
      console.log(`${config.displayName} date override:`, newDateRange);
      try {
        await fetchChartData(site, equipmentId, position, newDateRange);
      } catch (error) {
        console.error(`Failed to fetch ${config.displayName} data with custom date:`, error);
      }
    }
  }, [config.displayName, site, equipmentId, position, fetchChartData]);

  const handleResetToPageDate = useCallback(async () => {
    setChartDateOverride(null);
    setShowDatePicker(false);
    
    // Trigger chart-specific API call with page date range
    console.log(`${config.displayName} reset to page date:`, pageDateRange);
    if (site && equipmentId && position) {
      try {
        await fetchChartData(site, equipmentId, position, pageDateRange);
      } catch (error) {
        console.error(`Failed to fetch ${config.displayName} data with page date:`, error);
      }
    }
  }, [config.displayName, pageDateRange, site, equipmentId, position, fetchChartData]);

  // Custom tooltip formatting
  const formatTooltip = useCallback(({ datum }: any) => {
    // Comprehensive validation
    if (!datum || 
        datum.x === undefined || datum.x === null || 
        datum.y === undefined || datum.y === null ||
        isNaN(datum.y)) {
      return null; // Return null to hide tooltip for invalid data
      //TODO: Need to check why we are getting invalid data
    }
    
    try {
      // Validate date
      const dateValue = new Date(datum.x);
      if (isNaN(dateValue.getTime())) {
        return null; // Invalid date, hide tooltip
      }

      const date = dateValue.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const time = dateValue.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      });
      
      // Ensure value is a valid number
      const numericValue = Number(datum.y);
      if (isNaN(numericValue)) {
        return null;
      }
      
      const value = numericValue.toFixed(2);
      const unit = currentGraphData?.unit || '';
      
      return `${date} at ${time}\n${value} ${unit}`;
    } catch (error) {
      console.warn('Tooltip formatting error:', error);
      return null;
    }
  }, [currentGraphData]);

  // Filter out invalid data points for tooltip layer with enhanced validation
  const validTooltipData = useMemo(() => {
    if (!currentGraphData?.data) return [];
    
    return currentGraphData.data.filter(point => {
      if (!point || typeof point !== 'object') return false;
      if (point.x === undefined || point.x === null) return false;
      if (point.y === undefined || point.y === null) return false;
      
      // Validate Y value is a finite number
      const yValue = Number(point.y);
      if (!isFinite(yValue) || isNaN(yValue)) return false;
      
      // Validate X value can be converted to a valid date
      try {
        const dateTime = new Date(point.x).getTime();
        if (!isFinite(dateTime) || isNaN(dateTime)) return false;
      } catch (error) {
        return false;
      }
      
      return true;
    });
  }, [currentGraphData]);

  const syncTooltipMessage = useMemo(() => {
    if (isUsingPageDate) {
      return "Using the same date range as CBM Data Range above";
    } else {
      const startDate = new Date(currentDateRange.startDate).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric' 
      });
      const endDate = new Date(currentDateRange.endDate).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric' 
      });
      return `Using custom date range: ${startDate} to ${endDate}`;
    }
  }, [isUsingPageDate, currentDateRange]);

  // Determine what content to show in chart area
  const hasValidData = effectiveData && chartSeries && currentGraphData;
  const showError = effectiveError && !effectiveLoading;
  const showEmpty = !effectiveLoading && !effectiveError && !hasValidData;

  // Calculate responsive dimensions
  const getChartDimensions = () => {
    if (containerWidth >= 1200) {
      return { width: Math.min(containerWidth - 64, 1200), height: height + 50 };
    } else if (containerWidth >= 1024) {
      return { width: containerWidth - 48, height: height };
    } else if (containerWidth >= 768) {   // Tablet-specific range
      return { width: Math.min(containerWidth - 48, 900), height: height };  // Keep 900px limit for tablets
    } else {
      return { width: containerWidth - 32, height: height - 50 };
    }
  };

  const { width: chartWidth, height: chartHeight } = getChartDimensions();

  return (
    <Column spacing="400">
      {/* Chart Container */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        padding: containerWidth >= 1200 ? '32px' : '16px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        width: '100%',
        minWidth: `${chartWidth + 64}px`,  // Ensure consistent width for both technologies
        maxWidth: containerWidth >= 1200 ? '1200px' : 'none',
      }}>
        {/* Header with Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '32px',
          paddingBottom: '16px',
          borderBottom: '1px solid #f1f3f4'
        }}>
          <div>
            <Heading level={4}>
              {config.displayName} {config.chartMode === 'dual' && currentGraphData ? `- ${currentGraphData.label} Analysis` : ''}
            </Heading>
            <Row alignmentVertical="center" spacing="200">
              <Text type="b200" color="secondary">
                {currentGraphData ? (
                  dataMode === 'actual' 
                    ? `${currentGraphData.data.length} data points` 
                    : `${currentGraphData.data.length} trend points (downsampled)`
                ) : 'No data available'}
              </Text>
              {zoomDisplayText && (
                <Text type="b200" color="secondary">
                  • {zoomDisplayText}
                </Text>
              )}
              {/* Sync Status Badge */}
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <Text type="b200" color="secondary">•</Text>
                <div 
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: isUsingPageDate ? '#d4edda' : '#e2e3e5',
                    color: isUsingPageDate ? '#155724' : '#6c757d',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: 500,
                    border: `1px solid ${isUsingPageDate ? '#c3e6cb' : '#d1d3d4'}`,
                    cursor: 'help',
                    marginLeft: '8px'
                  }}
                >
                  <div style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: isUsingPageDate ? '#28a745' : '#6c757d',
                    marginRight: '4px'
                  }} />
                  {isUsingPageDate ? 'Synced' : 'Custom'}
                </div>

                {/* Custom Tooltip */}
                {showTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#333333',
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '11px',
                    fontWeight: 500,
                    zIndex: 1000,
                    marginBottom: '6px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    border: '1px solid #555555'
                  }}>
                    {syncTooltipMessage}
                    {/* Tooltip Arrow */}
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid #333333'
                    }} />
                  </div>
                )}
              </div>
            </Row>
          </div>
          
          {/* Controls Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: containerWidth < 768 ? 'wrap' : 'nowrap'
          }}>
            {/* Data Mode Toggle */}
            <div style={{
              display: 'flex',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '4px',
              border: '1px solid #e9ecef',
              minWidth: 'fit-content'
            }}>
              <button
                onClick={() => setDataMode('actual')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: dataMode === 'actual' ? '#0073bb' : 'transparent',
                  color: dataMode === 'actual' ? 'white' : '#6c757d',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  fontSize: '14px',
                  minWidth: '70px',
                  whiteSpace: 'nowrap'
                }}
              >
                Actual
              </button>
              <button
                onClick={() => setDataMode('trend')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: dataMode === 'trend' ? '#0073bb' : 'transparent',
                  color: dataMode === 'trend' ? 'white' : '#6c757d',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  fontSize: '14px',
                  minWidth: '70px',
                  whiteSpace: 'nowrap'
                }}
              >
                Trend
              </button>
            </div>

            {/* Zero Filter Toggle */}
            <button
              onClick={() => setZeroFilterMode(zeroFilterMode === 'include' ? 'exclude' : 'include')}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                backgroundColor: zeroFilterMode === 'exclude' ? '#0073bb' : '#f8f9fa',
                color: zeroFilterMode === 'exclude' ? 'white' : '#6c757d',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                minWidth: '100px'
              }}
            >
              {zeroFilterMode === 'include' ? 'Include Zero' : 'Exclude Zero'}
            </button>

            {/* Date Range Section */}
            <div style={{ 
              position: 'relative',
              minWidth: 'fit-content'
            }}>
              <Button
                type="secondary"
                size="small"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                🗓️ {isUsingPageDate ? 'Page Date' : 'Custom'}
              </Button>
              
              {showDatePicker && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  zIndex: 1000,
                  backgroundColor: 'white',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  minWidth: '300px'
                }}>
                  <Column spacing="200">
                    <Text type="b300">Select Date Range</Text>
                    <DateRangePicker
                      value={[currentDateRange.startDate, currentDateRange.endDate]}
                      onChange={handleDateRangeChange}
                      startLabel="Start date"
                      endLabel="End date"
                      locale="en-GB"
                      monthsInView={1}
                      presets={datePresets}
                      size="small"
                    />
                    <Row spacing="100">
                      {!isUsingPageDate && (
                        <Button
                          type="tertiary"
                          size="small"
                          onClick={handleResetToPageDate}
                        >
                          Reset to Current Page Date
                        </Button>
                      )}
                      <Button
                        type="tertiary"
                        size="small"
                        onClick={() => setShowDatePicker(false)}
                      >
                        Cancel
                      </Button>
                    </Row>
                  </Column>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Graph Type Selector (only for dual chart mode) */}
        {config.showGraphToggle && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '4px',
              border: '1px solid #e9ecef'
            }}>
              {config.series.map(series => (
                <button
                  key={series.name}
                  onClick={() => setCurrentGraphType(series.name)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: currentGraphType === series.name ? '#0073bb' : 'transparent',
                    color: currentGraphType === series.name ? 'white' : '#495057',
                    fontWeight: currentGraphType === series.name ? 600 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '14px'
                  }}
                >
                  {series.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Multi-Series Toggle (only for multi chart mode) */}
        {config.showMultiSeriesToggle && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {config.series.map(series => (
                <button
                  key={series.name}
                  onClick={() => toggleSeries(series.name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    border: `1px solid ${visibleSeries.includes(series.name) ? series.color : '#e9ecef'}`,
                    backgroundColor: visibleSeries.includes(series.name) ? `${series.color}15` : '#ffffff',
                    color: visibleSeries.includes(series.name) ? series.color : '#6c757d',
                    fontWeight: visibleSeries.includes(series.name) ? 600 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    fontSize: '13px',
                    minHeight: '36px'
                  }}
                >
                  <div style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '3px',
                    border: `2px solid ${visibleSeries.includes(series.name) ? series.color : '#dee2e6'}`,
                    backgroundColor: visibleSeries.includes(series.name) ? series.color : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease'
                  }}>
                    {visibleSeries.includes(series.name) && (
                      <div style={{
                        width: '8px',
                        height: '5px',
                        borderLeft: '2px solid white',
                        borderBottom: '2px solid white',
                        transform: 'rotate(-45deg) translateY(-1px)',
                      }} />
                    )}
                  </div>
                  {series.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chart Area with Loading/Error/Empty States */}
        <div style={{ position: 'relative', minHeight: chartHeight }}>
          {/* Loading Overlay */}
          {effectiveLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '8px'
            }}>
              <Column alignmentHorizontal="center" spacing="200">
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #0073bb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <Text type="b200" color="secondary">Loading {config.displayName} data...</Text>
              </Column>
            </div>
          )}

          {/* Error State */}
          {showError ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: chartHeight,
              padding: '32px'
            }}>
              <ChartErrorState config={config} error={effectiveError!} />
            </div>
          ) : showEmpty ? (
            /* Empty State */
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: chartHeight,
              padding: '32px'
            }}>
              <ChartEmptyState config={config} />
            </div>
          ) : hasValidData && currentGraphData ? (
            /* Chart Content */
            <>
              <VictoryChart
                key={`chart-${dataMode}-${visibleSeries.join('-')}`}
                theme={theme}
                width={chartWidth}
                height={chartHeight}
                scale={{ x: "time" }}
                containerComponent={zoomContainer}
                padding={{ 
                  left: containerWidth >= 1200 ? 120 : 100, 
                  top: 50, 
                  right: config.dualAxis ? (containerWidth >= 1200 ? 120 : 100) : 50, 
                  bottom: 80 
                }}
              >
                {/* Clean X Axis with Better Spacing */}
                <VictoryAxis
                  dependentAxis={false}
                  tickCount={5}
                  tickFormat={(x: any) => {
                    const date = new Date(x);
                    return date.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric'
                    });
                  }}
                  fixLabelOverlap={true}
                  style={{
                    axis: { stroke: "#dee2e6", strokeWidth: 1 },
                    tickLabels: { 
                      fontSize: 11, 
                      fill: "#333333", 
                      fontWeight: 500
                    },
                    grid: { stroke: "#f8f9fa", strokeWidth: 1 }
                  }}
                />

                {/* Left Y Axis (Velocity) */}
                <VictoryAxis
                  dependentAxis
                  orientation="left"
                  style={{
                    axis: { stroke: "#dee2e6", strokeWidth: 1 },
                    tickLabels: { 
                      fill: "#333333", 
                      fontSize: 11, 
                      fontWeight: 500
                    },
                    grid: { stroke: "#e9ecef", strokeWidth: 1, strokeDasharray: "2,2" }
                  }}
                  tickFormat={(y) => `${y.toFixed(1)}`}
                />

                {/* Right Y Axis (Temperature) - only if dual axis enabled */}
                {config.dualAxis && visibleSeriesData.some(series => series.axis === 'right') && (
                  <VictoryAxis
                    dependentAxis
                    orientation="right"
                    style={{
                      axis: { stroke: "#dee2e6", strokeWidth: 1 },
                      tickLabels: { 
                        fill: "#333333", 
                        fontSize: 11, 
                        fontWeight: 500
                      },
                      grid: { stroke: "none" } // Don't show grid for right axis to avoid confusion
                    }}
                    tickFormat={(y) => `${y.toFixed(0)}`}
                  />
                )}

                {/* ISO Line - Full Chart Width (only if configured and velocity data is visible) */}
                {isoLineData && visibleSeriesData.some(series => series.axis === 'left') && (
                  <VictoryLine
                    data={isoLineData}
                    style={{
                      data: { 
                        stroke: isoLineColor, 
                        strokeWidth: 2,
                        strokeDasharray: "5,5"
                      }
                    }}
                  />
                )}

                {/* Render all visible series lines */}
                {visibleSeriesData.map((series) => (
                  <VictoryLine
                    key={series.name}
                    data={series.data}
                    style={{
                      data: { 
                        stroke: series.color, 
                        strokeWidth: 2,
                        strokeLinecap: "round"
                      }
                    }}
                    animate={false}
                  />
                ))}

                {/* Invisible scatter points for tooltip hover detection for each visible series */}
                {visibleSeriesData.map((series) => {
                  const validData = series.data.filter(point => {
                    if (!point || typeof point !== 'object') return false;
                    if (point.x === undefined || point.x === null) return false;
                    if (point.y === undefined || point.y === null) return false;
                    
                    const yValue = Number(point.y);
                    if (!isFinite(yValue) || isNaN(yValue)) return false;
                    
                    try {
                      const dateTime = new Date(point.x).getTime();
                      if (!isFinite(dateTime) || isNaN(dateTime)) return false;
                    } catch (error) {
                      return false;
                    }
                    
                    return true;
                  });

                  return validData.length > 0 ? (
                    <VictoryScatter
                      key={`tooltip-${series.name}`}
                      data={validData}
                      size={6}
                      style={{
                        data: {
                          fill: 'transparent',
                          stroke: 'transparent'
                        }
                      }}
                      labelComponent={
                        <VictoryTooltip
                          flyoutStyle={{
                            stroke: series.color,
                            strokeWidth: 1,
                            fill: '#ffffff',
                            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))'
                          }}
                          style={{
                            fill: '#333333',
                            fontSize: 12,
                            fontWeight: 500,
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}
                          cornerRadius={6}
                          pointerLength={8}
                          flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                          constrainToVisibleArea
                        />
                      }
                      labels={formatTooltip}
                    />
                  ) : null;
                })}
              </VictoryChart>
              
              {/*Legend */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                marginTop: '20px',
                paddingTop: '12px',
                borderTop: '1px solid #f1f3f4'
              }}>
                {/* Show all visible series */}
                {visibleSeriesData.map(series => (
                  <div key={series.name} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <div style={{
                      width: 10,
                      height: 2,
                      backgroundColor: series.color,
                      borderRadius: '1px'
                    }} />
                    <Text type="b200" color="secondary">
                      {series.label} ({series.unit})
                    </Text>
                  </div>
                ))}
                
                {/* ISO Line Legend */}
                {isoLineData && visibleSeriesData.some(series => series.axis === 'left') && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <div style={{
                      width: 10,
                      height: 2,
                      backgroundColor: isoLineColor,
                      borderRadius: '1px'
                    }} />
                    <Text type="b200" color="secondary">
                      ISO Line ({config.isoLineValue})
                    </Text>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Add CSS animation for loading spinner */}
        {/* We can move the css to different file Also */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </Column>
  );
};
